import { array, fn } from '@ember/helper';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { EmblemType, TeamSectionItemType } from 'webapp/enums/enums';

import joinClasses from 'webapp/helpers/join-classes';
import visibleCheckItem from 'webapp/modifiers/visible-check-item';
import BoardImagePreviewComponent from 'webapp/components/board/image-preview';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import DummyCellComponent from 'webapp/components/dummy-cell';
import EditableModelPropertyComponent from 'webapp/components/editable-model-property';
import FlexibleToggleCellComponent from 'webapp/components/flexible-toggle-cell';
import FormCheckboxComponent from 'webapp/components/form/checkbox';
import OptionsButtonComponent from 'webapp/components/options-button';
import OwnerSelectComponent from 'webapp/components/owner-select';
import TagContainerComponent from 'webapp/components/tag-container';
import ToolTipComponent from 'webapp/components/tool-tip';
import UsedSourcesListComponent from 'webapp/components/used-sources-list';

import type BatchSelect from 'webapp/models-custom/batch-select';
import type { Action, Option } from 'webapp/models-custom/board-loop';
import type BoardLoop from 'webapp/models-custom/board-loop';

export interface Args {
	model: BoardLoop;
	batchSelect: BatchSelect<BoardLoop>;
	onOpenPreview: (model: BoardLoop) => void;
	onEdit: (model: BoardLoop) => void;
	onToggleFavorite: (model: BoardLoop) => void;
	onShare: (model: BoardLoop) => void;
	onOpenFullscreenView: (model: BoardLoop) => void;
	onOpenInDesigner: (model: BoardLoop) => void;
	onDuplicate: (model: BoardLoop) => void;
	onCreateReport: (model: BoardLoop) => void;
	onDelete: (model: BoardLoop) => void;
	onSaveAsTemplate: (model: BoardLoop) => void;
	onRestore: (model: BoardLoop) => void;
	onDeletePermanently: (model: BoardLoop, onPermanentDelete?: () => void) => void;
	onSingleItemRestore?: () => void;
	onSingleItemPermanentDelete?: () => void;
}

export default class BoardLoopsIndexTableRowComponent extends Component<Args> {
	@service('model-manager/activity-logs')
	declare mmActivityLogsService: Service.ModelManager.ActivityLogs;
	@service('account') declare accountService: Service.Account;
	@service('model-manager/team-sections')
	declare mmTeamSectionsService: Service.ModelManager.TeamSections;
	@service('model-manager/notifications')
	declare mmNotificationsService: Service.ModelManager.Notifications;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;

	@tracked isVisibleOnScreen = false;
	@tracked showAll = false;

	get options(): Option[] {
		return this.args.model.getAdditionalOptions();
	}

	get canEditBoardLoopName(): boolean {
		return this.args.model.canCurrentUserEdit && !this.accountService.isDemoAccount;
	}

	get isSelected(): boolean {
		const { batchSelect, model } = this.args;
		return batchSelect.isItemSelected(model);
	}

	get isDeleted() {
		return !!this.args.model.deleted;
	}

	get imageClickHandler() {
		if (this.isDeleted) {
			return;
		}
		return this.openPreview;
	}

	get emblemTypeName() {
		if (this.isDeleted) {
			return EmblemType.RECENTLY_DELETED;
		}

		return;
	}

	@action
	doOptionsAction(actionName: string): void {
		if (actionName !== 'openPreview' && this.accountService.handleDemo()) {
			return;
		}

		this[actionName as Action]();
	}

	@action
	openPreview() {
		this.args.onOpenPreview(this.args.model);
	}

	@action
	edit() {
		this.args.onEdit(this.args.model);
	}

	@action
	toggleFavorite() {
		this.args.onToggleFavorite(this.args.model);
	}

	@action
	share() {
		this.args.onShare(this.args.model);
	}

	@action
	openChangeOwnerPopup() {
		this.args.model.openChangeOwnerPopup();
	}

	@action
	openFullscreenView() {
		this.args.onOpenFullscreenView(this.args.model);
	}

	@action
	openInDesigner() {
		this.args.onOpenInDesigner(this.args.model);
	}

	@action
	duplicate() {
		this.args.onDuplicate(this.args.model);
	}

	@action
	createNotification() {
		this.mmBoardLoopsService.createAndOpenNewNotificationForModel(this.args.model, 'loops');
	}

	@action
	createReport() {
		this.args.onCreateReport(this.args.model);
	}

	@action
	addToTeam() {
		const itemId = this.args.model.id;

		if (itemId) {
			this.mmTeamSectionsService.openAddPopup({
				objectId: itemId,
				objectType: TeamSectionItemType.DATABOARD_LOOP,
			});
		}
	}

	@action
	openActivityLog() {
		this.mmActivityLogsService.createAndOpenPopup({
			model: this.args.model,
		});
	}

	@action
	saveAsTemplate() {
		this.args.onSaveAsTemplate(this.args.model);
	}

	@action
	openPublicTemplateEditPopup() {
		this.args.model.openPublicTemplateEditPopup();
	}

	@action
	deleteLoop() {
		this.args.batchSelect.unselectAll();
		this.args.onDelete(this.args.model);
	}

	@action
	restore() {
		this.args.onRestore(this.args.model);
		this.args.onSingleItemRestore?.();
	}

	@action
	deletePermanently() {
		this.args.onDeletePermanently(this.args.model, () => {
			this.args.onSingleItemPermanentDelete?.();
		});
	}

	@action
	visibleChanged(isVisible: boolean): void {
		this.isVisibleOnScreen = isVisible;
	}

	@action
	toggleShowAll() {
		this.showAll = !this.showAll;
	}
	<template>
		{{#if this.isVisibleOnScreen}}

			<tr
				class={{joinClasses
					(array
						"table-row" (if this.showAll "all-shown") (if this.isSelected "selected")
					)
				}}
				data-id={{@model.id}}
			>
				<td class="cc first">
					<FormCheckboxComponent
						@name="board-loops-table-row"
						@selected={{this.isSelected}}
						@theme="blue"
						@onChange={{fn @batchSelect.changeSelection @model}}
					/>
				</td>

				<td class="c0 board no-grow">
					<BoardImagePreviewComponent
						@image={{@model.image}}
						@color={{@model.color}}
						@theme={{@model.bgTheme}}
						@isLazyLoaded={{true}}
						@emblemTypeName={{this.emblemTypeName}}
						@onImageClick={{this.imageClickHandler}}
					/>
				</td>
				<td class="c1">

					<div class="flex">
						<EditableModelPropertyComponent
							@model={{@model}}
							@canEdit={{this.canEditBoardLoopName}}
							@editableProperty="title"
						/>

						<div class="labelicons">
							{{#if @model.favorite}}
								<span class="labelicon">
									<CommonUiIconComponent @name="StarFill" @size="sm" />
								</span>
							{{/if}}
						</div>
					</div>

					<small>by {{@model.createdByUser.name}} on {{@model.createdText}}</small>
				</td>

				<td class="prio-medium" data-label="# of dashboards: ">
					<span>
						{{@model.boardCount}}
						{{#if @model.boardCount}}
							<ToolTipComponent
								@text={{@model.boardsTooltipText}}
								@position="bottom"
								@delay={{200}}
								@maxWidth={{180}}
							/>
						{{/if}}
					</span>
				</td>

				<td class="c2 prio-low" data-label="Data sources included: ">
					<UsedSourcesListComponent
						@sources={{@model.sourcesList}}
						@maxShownSourcesCount={{6}}
					/>
				</td>

				<td class="prio-medium" data-label="Owner: ">
					<OwnerSelectComponent
						@model={{@model}}
						@canEdit={{false}}
						@isOwnerNameHidden={{true}}
					/>
				</td>

				<td class="c5 prio-low" data-label="Tags: ">
					<div>
						<TagContainerComponent
							@model={{@model}}
							@canEdit={{@model.canCurrentUserEdit}}
						/>
					</div>
				</td>

				<td class="c6 button-ct last">
					<OptionsButtonComponent
						@options={{this.options}}
						@onAction={{this.doOptionsAction}}
						@hideDropdownSearch={{true}}
					/>
				</td>

				<FlexibleToggleCellComponent
					@showAll={{this.showAll}}
					@toggleShowAll={{this.toggleShowAll}}
				/>

			</tr>

		{{else}}
			<tr class="table-row" {{visibleCheckItem this.visibleChanged}}>
				<DummyCellComponent />
			</tr>

		{{/if}}
	</template>
}
